// 대화형 설문 패널의 상태·턴 로직(스트리밍 아님). 렌더(SurveyPanel)와 분리한 단일 훅.
//
// 낙관적 말풍선·중복 가드·jump nonce·StrictMode 중복시작 가드 등 미묘한 상태머신을
// 그대로 보유한다(동작 보존). 렌더 컴포넌트는 이 훅이 돌려주는 값만 그린다.

import { useEffect, useRef, useState } from 'react'
import { surveyTurn } from '~/api/assessment'
import { errMessage } from '~/lib/errors'
import type {
    ChecklistField,
    ChecklistItem,
    SurveyAnswer,
    SurveyTurn,
    SurveyTurnRequest,
} from '~/api/types'

/** 대화 한 줄(말풍선). 사용자/AI 구분. */
interface Bubble {
    id: number
    role: 'user' | 'assistant'
    content: string
    optimistic?: boolean
}

export interface UseSurveyPanelParams {
    assessmentId: string
    /** 매 턴 결과를 상위로 올려 우측 현재 문항/진행도를 동기화한다. */
    onTurn(turn: SurveyTurn, options?: { refreshAnswers?: boolean }): void
    /** 채점 진행 중 표시(상위에서 제어). */
    scoring?: boolean
    /** 우측 전체 문항에서 선택한 문항으로 이동 요청. */
    jumpTarget?: { itemId: string; message: string; nonce: number } | null
    /** 서버 턴 응답 전 먼저 표시할 수 있는 체크리스트 스냅샷. */
    initialChecklist?: ChecklistItem[] | null
    /** 서버 턴 응답 전 진행도를 계산할 답안 스냅샷. */
    initialAnswers?: SurveyAnswer[]
    /** 업종 확인 전에는 체크리스트 질문을 먼저 표시하지 않는다. */
    seedInitialSurvey?: boolean
}

function answerKey(item: ChecklistItem, field?: ChecklistField): string {
    return field?.fieldId ?? `item:${item.id}`
}

function fieldDefinition(field: ChecklistField): string | undefined {
    if (typeof field.threshold === 'string') return field.threshold
    if (field.threshold == null) return undefined
    return String(field.threshold)
}

function makeInitialTurn(
    checklist: ChecklistItem[] | null | undefined,
    answers: SurveyAnswer[] | undefined
): SurveyTurn | null {
    if (!checklist?.length) return null

    const answered = new Set((answers ?? []).map((answer) => answer.fieldId))
    let progressAnswered = 0
    let progressTotal = 0
    let first: {
        item: ChecklistItem
        field?: ChecklistField
        fieldIndex: number
    } | null = null

    for (const item of checklist) {
        const fields: Array<ChecklistField | undefined> =
            item.fields.length > 0 ? item.fields : [undefined]
        for (let fieldIndex = 0; fieldIndex < fields.length; fieldIndex += 1) {
            const field = fields[fieldIndex]
            progressTotal += 1
            const key = answerKey(item, field)
            if (answered.has(key)) {
                progressAnswered += 1
                continue
            }
            first ??= { item, field, fieldIndex }
        }
    }

    if (!first) {
        return {
            phase: 'survey',
            assistantMessage: '설문을 모두 마쳤어요. 채점을 진행할까요?',
            progressAnswered,
            progressTotal,
            industryOptions: [],
            done: progressTotal > 0,
        }
    }

    const field = first.field
    const assistantMessage =
        field?.question?.trim() ||
        first.item.title ||
        '현재 문항에 답해 주세요.'

    return {
        phase: 'survey',
        assistantMessage,
        progressAnswered,
        progressTotal,
        currentItem: first.item,
        currentField: field
            ? {
                  fieldId: field.fieldId,
                  nameKo: field.nameKo,
                  definition: fieldDefinition(field),
                  unit: field.unit,
                  index: first.fieldIndex,
                  of: first.item.fields.length,
              }
            : undefined,
        industryOptions: [],
        done: false,
    }
}

/**
 * 대화형 설문 패널 상태 훅.
 * - phase==="industry": proposedIndustry 확인 + industryOptions 드롭다운 폴백.
 * - phase==="survey": 자유서술 답변 → 다음 문항.
 * - done: 입력을 막고 "채점하기" 노출.
 */
export function useSurveyPanel({
    assessmentId,
    onTurn,
    scoring,
    jumpTarget,
    initialChecklist,
    initialAnswers,
    seedInitialSurvey,
}: UseSurveyPanelParams) {
    const [bubbles, setBubbles] = useState<Bubble[]>([])
    const [turn, setTurn] = useState<SurveyTurn | null>(null)
    const [draft, setDraft] = useState('')
    const [selectedIndustry, setSelectedIndustry] = useState('')
    const [sending, setSending] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const bubbleSeq = useRef(0)
    const scrollRef = useRef<HTMLDivElement>(null)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    // StrictMode 이중 실행/리렌더에도 초기 설문 시작을 한 번만 보장한다.
    const startedRef = useRef(false)
    const seededRef = useRef(false)
    const optimisticBubbleRef = useRef<number | null>(null)
    const handledJumpNonceRef = useRef<number | null>(null)

    const pushBubble = (
        role: Bubble['role'],
        content: string,
        optimistic = false
    ) => {
        if (!content) return null
        const id = bubbleSeq.current++
        setBubbles((prev) => [...prev, { id, role, content, optimistic }])
        return id
    }

    // 매 턴 응답을 공통 처리: 말풍선 추가 + 상위 동기화 + 상태 저장.
    const applyTurn = (next: SurveyTurn) => {
        const optimisticId = optimisticBubbleRef.current
        if (optimisticId != null) {
            setBubbles((prev) =>
                prev.map((bubble) =>
                    bubble.id === optimisticId
                        ? {
                              ...bubble,
                              content: next.assistantMessage,
                              optimistic: false,
                          }
                        : bubble
                )
            )
            optimisticBubbleRef.current = null
        } else {
            // 직전 AI 말풍선과 내용이 같으면 중복 추가하지 않는다(초기 seed↔서버 턴 경합이나
            // 같은 문항 재도착으로 동일 질문이 두 번 뜨는 것을 방지). 정상 흐름에선 답변(사용자
            // 말풍선)이 사이에 끼므로 이 가드에 걸리지 않는다. 직전이 낙관적 seed였다면 확정만 한다.
            setBubbles((prev) => {
                if (!next.assistantMessage) return prev
                const lastIdx = prev.length - 1
                const last = prev[lastIdx]
                if (
                    last &&
                    last.role === 'assistant' &&
                    last.content === next.assistantMessage
                ) {
                    if (!last.optimistic) return prev
                    const copy = prev.slice()
                    copy[lastIdx] = { ...last, optimistic: false }
                    return copy
                }
                return [
                    ...prev,
                    {
                        id: bubbleSeq.current++,
                        role: 'assistant',
                        content: next.assistantMessage,
                    },
                ]
            })
        }
        setTurn(next)
        setSelectedIndustry('')
        onTurn(next)
    }

    const runTurn = async (body: SurveyTurnRequest) => {
        setSending(true)
        setError(null)
        try {
            const next = await surveyTurn(assessmentId, body)
            applyTurn(next)
        } catch (err: unknown) {
            setError(
                errMessage(err, '설문을 진행하지 못했어요. 다시 시도해 주세요.')
            )
        } finally {
            setSending(false)
        }
    }

    // 이미 받아온 체크리스트에 사전 생성 질문이 있으면 서버 턴 계산 전에 먼저 보여준다.
    useEffect(() => {
        if (!seedInitialSurvey || seededRef.current || turn) return
        const initialTurn = makeInitialTurn(initialChecklist, initialAnswers)
        if (!initialTurn) return

        const id = pushBubble('assistant', initialTurn.assistantMessage, true)
        if (id != null) optimisticBubbleRef.current = id
        seededRef.current = true
        setTurn(initialTurn)
        onTurn(initialTurn, { refreshAnswers: false })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seedInitialSurvey, initialChecklist, initialAnswers, turn])

    // 첫 진입 시 빈 바디로 설문 시작(업종 질문부터). ref 가드로 중복 시작을 막는다.
    useEffect(() => {
        if (startedRef.current) return
        startedRef.current = true
        void runTurn({})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assessmentId])

    // 새 말풍선이 추가되면 맨 아래로 스크롤.
    useEffect(() => {
        const el = scrollRef.current
        if (el) el.scrollTop = el.scrollHeight
    }, [bubbles])

    // 턴이 끝나면 입력창 포커스를 유지한다(전송 후 다시 클릭하지 않도록).
    useEffect(() => {
        if (!sending && !turn?.done && !scoring) {
            textareaRef.current?.focus()
        }
    }, [sending, turn, scoring])

    useEffect(() => {
        if (!jumpTarget || sending || scoring) return
        if (handledJumpNonceRef.current === jumpTarget.nonce) return
        handledJumpNonceRef.current = jumpTarget.nonce
        pushBubble('user', jumpTarget.message)
        void runTurn({
            navigate: 'item',
            targetItemId: jumpTarget.itemId,
            currentItemId: turn?.currentItem?.id,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jumpTarget?.nonce, sending, scoring])

    const done = turn?.done ?? false
    const phase = turn?.phase
    const currentItemId = turn?.currentItem?.id
    const locked = sending || scoring // done이어도 답 수정/이동 가능하게 잠그지 않는다.
    const inSurvey = turn != null && phase !== 'industry'

    const sendMessage = (text: string) => {
        const trimmed = text.trim()
        if (!trimmed || locked) return
        pushBubble('user', trimmed)
        setDraft('')
        void runTurn({ message: trimmed, currentItemId })
    }

    const confirmIndustry = (industry: string) => {
        if (!industry || locked) return
        pushBubble('user', `${industry}(으)로 진행할게요`)
        void runTurn({ industry })
    }

    const navTo = (navigate: 'prev' | 'next') => {
        if (locked) return
        // 이동도 대화에 남겨 좌측 챗과 우측 현재 문항을 일치시킨다.
        pushBubble('user', navigate === 'prev' ? '← 이전 문항' : '다음 문항 →')
        void runTurn({ navigate, currentItemId })
    }

    // 가장 마지막 AI 말풍선 인덱스(이 한 건만 타이핑 효과). optimistic seed는 제외해 판별.
    const lastAssistantIndex = bubbles.reduce(
        (last, bubble, index) =>
            bubble.role === 'assistant' && !bubble.optimistic ? index : last,
        -1
    )

    const showIndustryHelpers =
        phase === 'industry' &&
        turn != null &&
        !done &&
        (turn.proposedIndustry != null || turn.industryOptions.length > 0)

    // 선택형 HITL — 현재 질문이 여부형(예/아니오)이면 클릭 칩 제공. 그 외엔 '모름' 빠른 답.
    const currentQ = turn?.assistantMessage ?? ''
    const isYesNo =
        /예\s*\/\s*아니[오요]/.test(currentQ) ||
        /\(여부\)/.test(turn?.currentField?.nameKo ?? '')
    const answerChoices =
        !inSurvey || done
            ? []
            : isYesNo
              ? ['예', '아니오', '모름/해당없음']
              : ['모름/해당없음']

    return {
        bubbles,
        turn,
        draft,
        setDraft,
        selectedIndustry,
        setSelectedIndustry,
        sending,
        error,
        scrollRef,
        textareaRef,
        done,
        phase,
        locked,
        inSurvey,
        lastAssistantIndex,
        showIndustryHelpers,
        answerChoices,
        sendMessage,
        confirmIndustry,
        navTo,
    }
}
